import * as THREE from 'three';

/**
 * The close-up look: a grain on every natural surface, and light coming through the thin ones.
 *
 * ## The problem it exists to solve
 *
 * A tuft of sedge and the lawn behind it are the same green, lit by the same sun, and shaded by the
 * same smooth normals — so at ten metres they are one green field with a silhouette drawn on it.
 * Colour cannot fix that: both really *are* that green, and pushing them apart makes a drawing of a
 * garden rather than a garden. What tells them apart in a photograph is **surface**. Grass is a dense
 * fine pile that scatters light evenly. A leaf mass is clumps with shadowed pockets between them, and
 * the leaves at its edge light up when the sun is behind them. Bark is fissured and lets nothing
 * through at all.
 *
 * So each material gets a grain of its own character, and the ones made of leaves get a transmission
 * term. Nothing here changes a hue.
 *
 * ## Why it is procedural, and injected
 *
 * Flora geometry carries no UVs — it is generated, not authored (`GeometryBuilder`) — so there is
 * nowhere to put a texture. The grain is therefore a noise field evaluated in the plant's own object
 * space, which needs no UVs, never seams, and costs no VRAM.
 *
 * It is injected into the stock physical shader for the reason `SurfaceTextures` gives: a bespoke
 * material would have to re-earn every shadow, fog and tone-mapping path in the scene and would fall
 * behind on the first engine upgrade. `tests/rendering/DetailShading.test.ts` asserts that every
 * anchor this module replaces still exists in three's own shader, so an upgrade that renames one
 * fails a test rather than silently rendering flat.
 *
 * ## Why one shared uniform
 *
 * Materials are per plant — a site is hundreds of them — so the toggle cannot be a walk of the scene
 * graph, and it must not be a recompile. Every injected program is handed {@link DetailShading.uniforms}
 * *by reference*, so switching detail off is a single number changing in one object that every
 * program already points at. The branch it gates is uniform across the draw, which is what makes the
 * off state genuinely free rather than merely invisible.
 */

/** Which surface a material is standing for. Each has its own frequency, relief, and transmission. */
export type GrainKind = 'leafy' | 'needled' | 'bladed' | 'bark' | 'turf';

export const DETAIL_STORAGE_KEY = 'landscape-glade.detail';

export interface GrainProfile {
  /**
   * Cycles per metre along each object axis, which is where the character lives. Leaves are roughly
   * isotropic clumps; needles and blades are stretched hard along the stem, so their `y` is a small
   * fraction of their `x`/`z` and the grain comes out as strands rather than as spots.
   */
  frequency: readonly [number, number, number];
  /**
   * Fold the noise about its midpoint, turning rounded lumps into creases. What separates fissured
   * bark and bladed grass from the soft clumping of a broadleaf canopy.
   */
  ridged: boolean;
  /** How far the grain may swing the albedo either side of unity. */
  albedo: number;
  /**
   * Relief, **in metres**. How deep the pockets between the leaves are, how proud the fissures in the
   * bark stand. Zero emits no normal perturbation at all.
   *
   * Metres and not a dimensionless strength, and the difference is not cosmetic. The perturbation
   * recovers a *world-space* gradient, so the tilt it produces is `relief × frequency` — and with a
   * turf field at 46 cycles per metre, a figure like `0.35` asks for a slope of sixteen and lays the
   * normal flat on its side. The lawn came back as television static, which is exactly what a randomly
   * oriented normal looks like. Stated as a depth, the numbers are answerable: a lawn's pile really is
   * about four millimetres, and it produces about a tenth of a radian of tilt, which is what it should.
   */
  bump: number;
  /**
   * Where the grain gives out, in metres: `near` for the relief, `far` for the tone.
   *
   * Two distances rather than one because they fail differently. Relief has to be gone before a
   * fragment covers a whole period of the field, or it boils; tone is low frequency, never shimmers,
   * and is what keeps a shrub from flattening into a cut-out when the camera backs off. The ground
   * carries both further than a plant does, because it is the surface still filling the frame at
   * fifty metres.
   */
  fade: { near: readonly [number, number]; far: readonly [number, number] };
  /** How much sun comes through from behind. Zero for anything woody or opaque. */
  translucency: number;
  /**
   * Push the lit side of the grain toward new growth — yellower and a shade warmer. Only the parts
   * actually made of leaves get it; bark and turf are 0.
   */
  freshness: number;
}

/**
 * The five surfaces, and the numbers that make each one itself.
 *
 * These were set by putting a sedge, a hosta, a yew and the lawn in one frame and turning each figure
 * up until the four stopped reading as one green, then backing off until nothing looked printed on.
 * The albedo swings are deliberately small: the grain is there to be *seen through*, and a garden
 * plan that reads as texture-mapped is further from a photograph than a flat one is.
 */
const PLANT_FADE = { near: [4, 16], far: [14, 40] } as const;

const PROFILES: Record<GrainKind, GrainProfile> = {
  // Broadleaf canopies and clumps: pockets of shadow between rounded masses of leaves.
  leafy: {
    frequency: [17, 21, 17],
    ridged: false,
    albedo: 0.16,
    // A centimetre between the face of a leaf and the shade behind the one next to it.
    bump: 0.012,
    translucency: 0.5,
    freshness: 0.05,
    fade: PLANT_FADE,
  },
  // Conifers: fine across the spray, drawn out along it, and creased rather than lumpy.
  needled: {
    frequency: [34, 9, 34],
    ridged: true,
    albedo: 0.13,
    bump: 0.008,
    translucency: 0.3,
    freshness: 0.02,
    fade: PLANT_FADE,
  },
  // Grasses and mats. The geometry already carries blades, so the grain adds the strand *within* a
  // blade and stops well short of competing with the silhouette.
  bladed: {
    frequency: [52, 2.4, 52],
    ridged: true,
    albedo: 0.15,
    bump: 0.006,
    translucency: 0.42,
    freshness: 0.04,
    fade: PLANT_FADE,
  },
  // Trunks and stems: vertical fissures, no transmission, and the strongest swing of the five —
  // bark is the one surface where the relief is most of what you are looking at.
  bark: {
    frequency: [22, 3, 22],
    ridged: true,
    albedo: 0.18,
    // The deepest of the five: a mature trunk's fissures really are a centimetre and a half.
    bump: 0.015,
    translucency: 0,
    freshness: 0,
    fade: PLANT_FADE,
  },
  // The lawn. Much finer and much quieter than anything a plant gets, which is the whole point: it
  // has to read as an even pile beside the clumping of a bed, not as another kind of leaf.
  turf: {
    frequency: [46, 46, 46],
    ridged: false,
    albedo: 0.085,
    // The pile of a mown lawn.
    bump: 0.004,
    translucency: 0,
    freshness: 0,
    fade: { near: [3, 14], far: [18, 55] },
  },
};

export function grainProfile(kind: GrainKind): GrainProfile {
  return PROFILES[kind];
}

function readStored(): boolean {
  try {
    // Absent means "never chosen", and the answer for that is on — a look nobody can see until they
    // find a button is a look nobody has.
    return globalThis.localStorage?.getItem(DETAIL_STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}

/** The switch, and the one uniform object every injected program is bound to. */
export class DetailShading {
  /** Handed to shaders by reference — see the note at the top of this file. */
  readonly uniforms: { uLgDetail: { value: number } };

  private on: boolean;
  private readonly listeners = new Set<(on: boolean) => void>();

  constructor(enabled = readStored()) {
    this.on = enabled;
    this.uniforms = { uLgDetail: { value: enabled ? 1 : 0 } };
  }

  get enabled(): boolean {
    return this.on;
  }

  setEnabled(enabled: boolean): void {
    if (enabled === this.on) return;
    this.on = enabled;
    this.uniforms.uLgDetail.value = enabled ? 1 : 0;
    try {
      globalThis.localStorage?.setItem(DETAIL_STORAGE_KEY, enabled ? 'on' : 'off');
    } catch {
      // Non-fatal: the choice simply does not survive the session.
    }
    for (const listener of this.listeners) listener(enabled);
  }

  onChange(listener: (on: boolean) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

/** The one in force. The terrain and every flora material bind to this instance's uniforms. */
export const detailShading = new DetailShading();

/* ------------------------------------- The shader ------------------------------------- */

/**
 * Value noise, and the two globals every injection writes through.
 *
 * Three octaves, hashed rather than sampled: a texture would need a UV the flora geometry does not
 * have, and at these frequencies the third octave is already below a pixel at any distance worth
 * drawing it from.
 *
 * `lgHeight` is a file-scope global rather than a local because the grain is computed with the colour
 * and read again when the normal is perturbed, two chunks later — and the derivative taken there must
 * see the same value this fragment wrote.
 */
export function detailNoiseGlsl(): string {
  return `
uniform float uLgDetail;
float lgHeight;
float lgNearFade;
float lgFarFade;
// The multiplier and the offsets are not arbitrary and were not free. A first pass used a smaller
// scale and round offsets, and the lawn came back streaked diagonally at 45° — the hash was
// correlating along the axes, and a garden's ground is the one surface wide enough and flat enough to
// show it. These are the standard constants for this hash; they cost nothing and are isotropic.
float lgHash( vec3 p ) {
  p = 50.0 * fract( p * 0.3183099 + vec3( 0.71, 0.113, 0.419 ) );
  return fract( p.x * p.y * p.z * ( p.x + p.y + p.z ) );
}
float lgNoise( vec3 x ) {
  vec3 i = floor( x );
  vec3 f = fract( x );
  f = f * f * ( 3.0 - 2.0 * f );
  return mix(
    mix(
      mix( lgHash( i ), lgHash( i + vec3( 1.0, 0.0, 0.0 ) ), f.x ),
      mix( lgHash( i + vec3( 0.0, 1.0, 0.0 ) ), lgHash( i + vec3( 1.0, 1.0, 0.0 ) ), f.x ),
      f.y
    ),
    mix(
      mix( lgHash( i + vec3( 0.0, 0.0, 1.0 ) ), lgHash( i + vec3( 1.0, 0.0, 1.0 ) ), f.x ),
      mix( lgHash( i + vec3( 0.0, 1.0, 1.0 ) ), lgHash( i + vec3( 1.0, 1.0, 1.0 ) ), f.x ),
      f.y
    ),
    f.z
  );
}
float lgFbm( vec3 p ) {
  return lgNoise( p ) * 0.6 + lgNoise( p * 2.7 + 11.3 ) * 0.3 + lgNoise( p * 6.1 + 27.7 ) * 0.1;
}`;
}

/**
 * How far the folded field sits below the plain one, measured rather than reasoned about.
 *
 * Folding the noise about its midpoint does not just change its shape, it moves its mean: the plain
 * field averages 0.500, the folded one 0.203. Since the grain is applied as a swing either side of
 * 0.5, a ridged surface left uncorrected comes out uniformly *darker* than the smooth one beside it —
 * nine percent for a grass blade, eleven for bark. That is not texture, it is a tint, and it was
 * plainly visible as switchgrass going several shades down the moment the switch was thrown.
 *
 * Both figures are from 200k samples of the field itself. Change the octave weights and this constant
 * has to be measured again.
 */
const RIDGE_RECENTRE = 0.297;

/** The grain of one surface, as a function of a position in metres. Result is in 0..1, mean 0.5. */
export function grainGlsl(kind: GrainKind): string {
  const { frequency, ridged } = PROFILES[kind];
  const [x, y, z] = frequency;
  const field = `lgFbm( p * vec3( ${glsl(x)}, ${glsl(y)}, ${glsl(z)} ) )`;
  const body = ridged
    ? // Folded about the midpoint: peaks become creases, which is what a fissure and the edge of a
      // blade both are. Then lifted back onto the same mean as the plain field — see above. The clamp
      // catches the top half-percent of crests, which are meant to read as caught light anyway.
      `clamp( abs( ${field} - 0.5 ) * 2.0 + ${glsl(RIDGE_RECENTRE)}, 0.0, 1.0 )`
    : field;
  return `
float lgGrain( vec3 p ) {
  return ${body};
}`;
}

/**
 * Perturb the shading normal from the grain, without a normal map.
 *
 * This is three's own `perturbNormalArb` arithmetic — the surface gradient recovered from the screen-
 * space derivatives of a height and the view position — written out rather than borrowed, because the
 * height here is analytic and never passes through the bump-map chunk that would otherwise compute
 * it. Copying the maths is the smaller coupling: it is a fixed identity, where the chunk's internal
 * helper names are not.
 *
 * Every derivative is taken in uniform control flow and the *strength* is applied afterwards. Fading
 * the relief out with distance is not decoration — it is what stops a 50-cycle-per-metre field from
 * turning into a boiling sparkle the moment a fragment covers more than one period.
 */
function bumpGlsl(strength: number): string {
  return `
{
  vec3 lgSurf = - vViewPosition;
  vec3 lgSx = dFdx( lgSurf );
  vec3 lgSy = dFdy( lgSurf );
  vec2 lgDh = vec2( dFdx( lgHeight ), dFdy( lgHeight ) ) * ( ${glsl(strength)} * uLgDetail * lgNearFade );
  vec3 lgR1 = cross( lgSy, normal );
  vec3 lgR2 = cross( normal, lgSx );
  float lgDet = dot( lgSx, lgR1 ) * ( gl_FrontFacing ? 1.0 : -1.0 );
  vec3 lgGrad = sign( lgDet ) * ( lgDh.x * lgR1 + lgDh.y * lgR2 );
  normal = normalize( abs( lgDet ) * normal - lgGrad );
}`;
}

/**
 * Sun coming through the leaves.
 *
 * The term that most makes a bed read as a bed: a canopy with the sun behind it glows at its thin
 * edges, and no amount of surface grain says "this is made of leaves" as plainly. Added to the
 * indirect diffuse because that is what it physically is — light that entered the far side, scattered,
 * and left toward the eye — and adding it there means tone mapping and fog treat it like every other
 * bounce rather than like an emissive decal.
 *
 * Deliberately **not** shadowed. The shadow factor is consumed inside the light loop and is gone by
 * the time this runs, and re-deriving it would double the shadow-map cost of every leaf in the garden
 * to dim a term that is already small and only visible when the sun is nearly behind the plant. A
 * shaded shrub picks up a faint glow it has not earned; a lit one gets the effect that is worth the
 * whole feature.
 */
function translucencyGlsl(strength: number): string {
  return `
#if NUM_DIR_LIGHTS > 0
{
  vec3 lgL = directionalLights[ 0 ].direction;
  vec3 lgV = normalize( vViewPosition );
  // 1 where the sun sits directly behind what we are looking at, 0 where it is over our shoulder.
  float lgBack = clamp( dot( - lgL, lgV ), 0.0, 1.0 );
  // Steep, because a leaf only lights up through its thin dimension. Faces edge-on to the sun carry
  // the least material between it and the eye, so they carry the most of it.
  float lgThin = pow( lgBack, 3.0 ) * ( 0.55 + 0.45 * ( 1.0 - abs( dot( normal, lgL ) ) ) );
  reflectedLight.indirectDiffuse += diffuseColor.rgb * directionalLights[ 0 ].color *
    ( lgThin * ${glsl(strength)} * uLgDetail );
}
#endif`;
}

/**
 * Tint and shade the albedo by the grain.
 *
 * Two fades, and they are different on purpose. Relief has to go before the grain aliases, which is
 * close. Tone can stay much longer, and should: it is what keeps a shrub from flattening into a
 * cut-out the moment the camera backs off, and being low frequency it does not shimmer.
 */
function albedoGlsl(kind: GrainKind, positionExpr: string, weightExpr = '1.0'): string {
  const { albedo, freshness, fade } = PROFILES[kind];
  const fresh =
    freshness > 0
      ? `
  diffuseColor.rgb = max(
    vec3( 0.0 ),
    diffuseColor.rgb + vec3( 0.9, 0.7, -0.4 ) * ( ( lgHeight - 0.5 ) * ${glsl(freshness)} * lgAmount )
  );`
      : '';
  // The weight is folded into both fades rather than applied to the colour, so the relief injected
  // two chunks later is held back by exactly the same amount without having to be told about it.
  return `
{
  float lgDistance = length( vViewPosition );
  float lgWeight = ${weightExpr};
  lgNearFade = ( 1.0 - smoothstep( ${glsl(fade.near[0])}, ${glsl(fade.near[1])}, lgDistance ) ) * lgWeight;
  lgFarFade = ( 1.0 - smoothstep( ${glsl(fade.far[0])}, ${glsl(fade.far[1])}, lgDistance ) ) * lgWeight;
  lgHeight = lgGrain( ${positionExpr} );
  float lgAmount = uLgDetail * lgFarFade;
  diffuseColor.rgb *= 1.0 + ( lgHeight - 0.5 ) * 2.0 * ${glsl(albedo)} * lgAmount;${fresh}
}`;
}

/** Six decimal places and always a point, so every emitted literal is a valid GLSL float. */
function glsl(value: number): string {
  const text = value.toFixed(6).replace(/0+$/, '');
  return text.endsWith('.') ? `${text}0` : text;
}

/**
 * The three anchors in three's physical shader that a flora injection replaces, named once so the
 * upgrade test and the injection cannot disagree about them.
 */
export const DETAIL_FRAGMENT_ANCHORS = [
  '#include <color_fragment>',
  '#include <normal_fragment_maps>',
  '#include <lights_fragment_end>',
] as const;

export const DETAIL_VERTEX_ANCHORS = ['#include <common>', '#include <begin_vertex>'] as const;

/**
 * Everything a *flora* material needs, ready to be dropped into `onBeforeCompile`.
 *
 * Split out from {@link attachDetail} so the whole injection can be exercised as string arithmetic in
 * a test, with no GL context anywhere near it.
 */
export function injectDetail(
  shader: { vertexShader: string; fragmentShader: string },
  kind: GrainKind,
): void {
  const profile = PROFILES[kind];

  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', '#include <common>\nvarying vec3 vLgObject;')
    // Object space, not world: a plant carries its grain with it, so nudging a shrub across the bed
    // does not make its own surface crawl underneath it.
    .replace('#include <begin_vertex>', '#include <begin_vertex>\nvLgObject = transformed;');

  shader.fragmentShader = shader.fragmentShader
    .replace(
      '#include <common>',
      `#include <common>
varying vec3 vLgObject;
uniform vec3 uLgGrainOrigin;
${detailNoiseGlsl()}
${grainGlsl(kind)}`,
    )
    .replace(
      '#include <color_fragment>',
      `#include <color_fragment>\n${albedoGlsl(kind, 'vLgObject + uLgGrainOrigin')}`,
    );

  if (profile.bump > 0) {
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <normal_fragment_maps>',
      `#include <normal_fragment_maps>\n${bumpGlsl(profile.bump)}`,
    );
  }
  if (profile.translucency > 0) {
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <lights_fragment_end>',
      `#include <lights_fragment_end>\n${translucencyGlsl(profile.translucency)}`,
    );
  }
}

/**
 * The terrain's share: the grain expressions only, since `TerrainMaterial` owns its own injection and
 * has already sampled the painted layers by the time the turf is laid over what is left of them.
 */
export function turfGrainGlsl(): { common: string; albedo: string; bump: string } {
  return {
    common: `${detailNoiseGlsl()}\n${grainGlsl('turf')}`,
    // The ground's own metre-space coordinate, flattened into the noise field's third dimension, and
    // weighted by the share of the fragment that is still bare ground. A gravel bed or a flagstone
    // terrace has a photographed swatch of its own; laying a turf grain over that would be inventing
    // a pile the material does not have.
    albedo: albedoGlsl('turf', 'vec3( lgGround.x, 0.0, lgGround.y )', 'lgBase'),
    bump: bumpGlsl(PROFILES.turf.bump),
  };
}

/**
 * Bind a flora material to the detail shading.
 *
 * `origin` decorrelates neighbours. Without it every hosta in a drift carries the identical grain in
 * the identical place, and a bed of one species reads as a row of stamped copies — the exact fault
 * `colorVariation` exists to prevent, one level further down.
 */
export function attachDetail(
  material: THREE.MeshStandardMaterial,
  kind: GrainKind,
  seed = 0,
): void {
  const origin = new THREE.Vector3(
    Math.sin(seed * 12.9898) * 43.7,
    Math.sin(seed * 78.233) * 27.3,
    Math.sin(seed * 37.719) * 61.1,
  );
  const uniforms = { uLgGrainOrigin: { value: origin } };

  material.onBeforeCompile = (shader) => {
    // By reference, both of them: the shared switch so a toggle reaches a program three may have
    // handed back from its cache without re-running this function, and the origin because it is this
    // material's alone.
    Object.assign(shader.uniforms, detailShading.uniforms, uniforms);
    injectDetail(shader, kind);
  };
  // Programs are cached by source. Without a key of its own a leafy material could be handed the
  // bark program, which compiles perfectly and renders a shrub as a tree trunk.
  material.customProgramCacheKey = () => `lg-detail-${kind}`;
}
